<template>
  <b-card :title="titleTable">
    <!-- search input -->
    <div class="custom-search d-flex justify-content-start">
      <b-button
        v-if="showBtnAdd && canAdd"
        variant="primary"
        data-cy="incluir"
        @click="add()"
      >
        Incluir
      </b-button>
      <slot name="buttons" />
    </div>
    <div class="custom-search d-flex justify-content-end">
      <b-form-group>
        <div
          v-if="showSearch"
          class="d-flex align-items-center"
        >
          <label class="mr-1">{{ $t("Search") }}</label>
          <b-form-input
            v-model="searchTerm"
            placeholder="Search"
            type="text"
            class="d-inline-block"
            data-cy="search"
            @input="handleSearch"
          />
        </div>
      </b-form-group>
    </div>

    <!-- no permission -->
    <div
      v-if="!canView"
      class="text-center p-5 my-3"
    >
      <b-alert
        variant="warning"
        show
        class="mb-0"
      >
        <div
          class="alert-body d-flex align-items-center justify-content-center"
        >
          <feather-icon
            icon="LockIcon"
            size="24"
            class="mr-50"
          />
          <span class="font-weight-bolder mr-50">Acesso Restrito:</span>
          <span>Você não tem permissão para visualizar a lista de
            {{ titleTable }}</span>
        </div>
      </b-alert>
    </div>

    <!-- table -->
    <vue-good-table
      v-else
      mode="remote"
      :columns="columns"
      :rows="rows"
      :rtl="direction"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm,
      }"
      :pagination-options="{
        enabled: true,
        perPage: pageLength,
      }"
      :total-rows="totalRecords"
      :is-loading.sync="isLoading"
      @on-sort-change="onSortChange"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Column: Status -->
        <span
          v-if="
            props.column.field === 'ativo' ||
              props.column.field === 'lancarNoTitular' ||
              props.column.field === 'lancaMensalidade'
          "
        >
          <b-badge :variant="statusVariant(props.row[props.column.field])">
            {{ props.row[props.column.field] ? "Sim" : "Não" }}
          </b-badge>
        </span>

        <!-- Column: Action -->
        <span
          v-else-if="
            props.column.field === 'action' && (canEdit || canDelete || canView)
          "
        >
          <span>
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              data-cy="action"
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle mr-25"
                />
              </template>
              <b-dropdown-item
                data-cy="edit-btn"
                @click="edit(props.row)"
              >
                <feather-icon
                  :icon="canEdit ? 'Edit2Icon' : 'EyeIcon'"
                  class="mr-50"
                />
                <span data-cy="edit">{{
                  canEdit ? "Edit" : "Visualizar"
                }}</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="showActionDelete && canDelete"
                data-cy="delete"
                @click="remove(props.row)"
              >
                <feather-icon
                  icon="TrashIcon"
                  class="mr-50"
                />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
        </span>

        <!-- Column: Status -->
        <span v-else-if="props.column.field === 'matricula'">
          {{ mascaraMatricula(props.row.matricula) }}
        </span>

        <!-- Column: Categorias (tiposFaixaPadrao) -->
        <span v-else-if="props.column.field === 'tiposFaixaPadrao'">
          <b-badge
            v-for="faixa in props.row.tiposFaixaPadrao"
            :key="faixa.id"
            variant="light-info"
            class="mr-25 mb-25"
          >
            {{ faixa.nome || faixa.descricao }}
          </b-badge>
        </span>

        <!-- Column: logoUrl -->
        <span v-else-if="props.column.field === 'logoUrl'">
          <b-img
            v-if="props.row.logoUrl"
            :src="props.row.logoUrl"
            width="32"
            height="32"
            alt="Logo"
            class="rounded border shadow-sm"
          />
          <feather-icon
            v-else
            icon="ImageIcon"
            size="20"
            class="text-muted"
          />
        </span>

        <!-- Column: Custom Valor for Encargos -->
        <span
          v-else-if="props.column.field === 'valor' && props.row.tipoEncargo"
        >
          {{ props.row.tipoEncargo === "FIXO" ? "R$ " : "" }}
          {{ props.row.valor }}
          {{ props.row.tipoEncargo === "PERCENTUAL" ? "%" : "" }}
        </span>

        <!-- Column: Custom TipoEncargo Badge -->
        <span v-else-if="props.column.field === 'tipoEncargo'">
          <b-badge
            :variant="
              props.row.tipoEncargo === 'FIXO' ? 'light-primary' : 'light-info'
            "
          >
            {{ props.row.tipoEncargo }}
          </b-badge>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap"> Showing 1 to </span>
            <b-form-select
              v-model="tableConfigs.size"
              :options="[tableConfigs.size]"
              class="mx-1"
              @input="handleChangePage"
            />
            <span class="text-nowrap">
              of {{ tableConfigs.totalPages }} entries
            </span>
          </div>
          <div>
            <b-pagination
              :value="pageConfig.page"
              :total-rows="tableConfigs.totalElements"
              :per-page="tableConfigs.size"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @change="handleChangePage"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </b-card>
</template>

<script>
import {
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BDropdown,
  BDropdownItem,
  BButton,
  BCard,
  BImg,
  BAlert,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'
import _ from 'lodash'
import mixinsGlobal from '@/mixins'
import { verificarPermissao } from '@/auth/utils'

export default {
  components: {
    BCard,
    VueGoodTable,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    BButton,
    BImg,
    BAlert,
  },
  mixins: [mixinsGlobal],
  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    rows: {
      type: Array,
      default: () => [],
    },
    titleTable: {
      type: String,
      default: 'Basic Table',
    },
    sortName: {
      type: String,
      default: '',
    },
    module: {
      type: String,
      required: true,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    showBtnAdd: {
      type: Boolean,
      default: true,
    },
    showActionDelete: {
      type: Boolean,
      default: true,
    },
    searchField: {
      type: String,
      default: 'nome',
    },
    viewPermission: {
      type: String,
      default: '',
    },
    editPermission: {
      type: String,
      default: '',
    },
    deletePermission: {
      type: String,
      default: '',
    },
    addPermission: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      totalRecords: 0,
      isLoading: false,
      verificarPermissao,
      tableConfigs: {
        last: false,
        totalPages: 5,
        totalElements: 41,
        size: 10,
        number: 0,
      },
      pageConfig: {
        page: 1,
        size: 10,
        sort: 'nome',
        order: 'asc',
      },
      pageLength: 10,
      dir: false,
      searchTerm: '',
      status: [
        {
          1: 'true',
          2: 'false',
        },
        {
          1: 'light-primary',
          2: 'light-danger',
        },
      ],
    }
  },
  computed: {
    canView() {
      if (this.viewPermission === null) return false
      if (!this.viewPermission) return true
      return verificarPermissao(this.viewPermission)
    },
    canEdit() {
      if (this.editPermission === null) return false
      if (!this.editPermission) return true
      return verificarPermissao(this.editPermission)
    },
    canDelete() {
      if (this.deletePermission === null) return false
      if (!this.deletePermission) return true
      return verificarPermissao(this.deletePermission)
    },
    canAdd() {
      if (this.addPermission === null) return false
      if (!this.addPermission) return true
      return verificarPermissao(this.addPermission)
    },
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        true: 'light-primary',
        false: 'light-danger',
        /* eslint-enable key-spacing */
      }

      return status => statusColor[status]
    },
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
  },
  mounted() {
    this.pageConfig.sort = this.sortName
    if (this.canView) {
      this.loadTable(this.module, this.pageConfig)
    }
  },
  methods: {
    add() {
      this.$emit('add')
    },
    edit(data) {
      this.$emit('edit', data)
    },
    async remove(data) {
      this.$swal({
        title: 'Remover o registro?',
        text: 'Está ação não poderá ser desfeita',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remover',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.$store
            .dispatch(`${this.module}/delete`, data.id)
            .then(response => {
              if (response.data.error) {
                this.MensagemError(
                  'Falha ao remover o registro',
                  response.data.message,
                )
                return
              }

              if (response.error) {
                this.MensagemError(
                  'Falha ao remover o registro',
                  response.message,
                )
                return
              }
              this.showMessageSuccess(
                'Remover',
                'Registro removido com sucesso!',
              )
              this.pageConfig.sort = this.sortName
              this.loadTable(this.module, this.pageConfig)
            })
            .catch(err => {
              this.MensagemError('Falha ao remover o registro', err.message)
              this.pageConfig.sort = this.sortName
              this.loadTable(this.module, this.pageConfig)
            })
        }
      })
    },
    handleChangePage(page) {
      this.pageConfig.page = page
      this.loadTable(this.module, this.pageConfig)
    },
    loadTable(module, pageConfig) {
      this.isLoading = true
      this.$store
        .dispatch(`${module}/loadTable`, pageConfig)
        .then(result => {
          this.pageConfig.page = (result.data.number || 0) + 1
          this.tableConfigs.last = result.data.last
          this.tableConfigs.totalPages = result.data.totalPages
          this.tableConfigs.totalElements = result.data.totalElements
          this.tableConfigs.size = result.data.size
          this.tableConfigs.number = result.data.number
          this.isLoading = false
        })
        .catch(err => {
          const errorMessage = err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : err.message
          const errorType = err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : 'Erro'

          this.MensagemError(
            'Falha ao carregar lista',
            `${errorType} / ${errorMessage}`,
          )
          this.isLoading = false
        })
    },
    onSortChange(params) {
      this.pageConfigdefault()
      this.pageConfig.sort = params[0].field
      this.pageConfig.order = params[0].type
      this.loadTable(this.module, this.pageConfig)
    },
    handleSearch: _.debounce(function (searching) {
      this.pageConfigdefault()
      if (searching) {
        this.pageConfig.filter = `${this.searchField}=${searching}`
      }
      this.loadTable(this.module, this.pageConfig)
    }, 300),
    pageConfigdefault() {
      this.pageConfig = {
        check: false,
        page: 1,
        size: 10,
        sort: this.sortName || 'id',
        order: 'asc',
      }
    },
    mascaraMatricula(value) {
      let matriculaMascarada = value
      if (matriculaMascarada) {
        matriculaMascarada = matriculaMascarada.replace(/\D/g, '') // Remove o que não é digito
        if (matriculaMascarada.length <= 4) {
          matriculaMascarada = matriculaMascarada.replace(
            /(\d{3,4})?(\d{2})$/,
            '$1-$2',
          )
        } else if (matriculaMascarada.length <= 7) {
          matriculaMascarada = matriculaMascarada.replace(
            /(\d{3,4})?(\d{2})/,
            '$1-$2',
          )
        } else if (matriculaMascarada.length >= 8) {
          matriculaMascarada = matriculaMascarada.replace(
            /(\d{2})?(\d{4})?(\d{2})$/,
            '$1.$2-$3',
          )
        }
      }
      return matriculaMascarada
    },
  },
}
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
</style>
